import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './AdminDripCampaignsPage.css';
import Button from '../components/Button';
import AdminPage from '../components/AdminShell';

// Audience options shown in the dropdowns. `athlete_guardian` is a composite
// audience the backend fans out to both the athlete and guardian roles.
const AUDIENCE_OPTIONS = [
  { value: 'athlete', label: 'Athlete' },
  { value: 'coach', label: 'Coach' },
  { value: 'partner', label: 'Partner' },
  { value: 'athlete_guardian', label: 'Athlete & Guardian' },
];
// Display labels for any stored audience value (includes legacy athlete wire
// values that collapse to "Athlete").
const AUDIENCE_LABELS = {
  athlete: 'Athlete',
  coach: 'Coach',
  partner: 'Partner',
  guardian: 'Guardian',
  player_parent: 'Athlete',
  base_user: 'Athlete',
  athlete_guardian: 'Athlete & Guardian',
};
const audienceLabel = (value) => AUDIENCE_LABELS[value] || value;
const UNIT_SECONDS = { Minutes: 60, Hours: 3600, Days: 86400 };

export default function AdminDripCampaignsPage() {
  const { API_URL } = useAuth();
  const getAxiosConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const [tab, setTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [editing, setEditing] = useState(null); // campaign object, {} for new, or null when closed
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, cond] = await Promise.all([
        axios.get(`${API_URL}/admin/drip-campaigns`, getAxiosConfig()),
        axios.get(`${API_URL}/admin/drip-campaigns/conditions`, getAxiosConfig()),
      ]);
      setCampaigns(c.data);
      setConditions(cond.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdminPage
      // No `active`: the rail has no Messages item, and drip campaigns is a
      // messaging surface (Layout.js files it under the Messages dropdown).
      // Highlighting the nearest item would point at the wrong page.
      title="Drip Campaigns"
      subtitle="Automated time-delayed in-app messages + role broadcasts."
    >
      {/* The page keeps its .admin-drip-page wrapper INSIDE the shell so its
          own stylesheet still applies to the tabs and editor below. */}
      <div className="admin-drip-page">
      <div className="adm-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'campaigns'}
          className={`adm-tab${tab === 'campaigns' ? ' adm-tab--active' : ''}`}
          onClick={() => setTab('campaigns')}
        >
          Campaigns
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'broadcast'}
          className={`adm-tab${tab === 'broadcast' ? ' adm-tab--active' : ''}`}
          onClick={() => setTab('broadcast')}
        >
          Broadcast (send now)
        </button>
      </div>
      {error && <div className="adm-error">{error}</div>}
      {tab === 'campaigns' && (
        <CampaignsTab
          campaigns={campaigns}
          loading={loading}
          onEdit={setEditing}
          onNew={() => setEditing({})}
          onDeleted={load}
          API_URL={API_URL}
          getAxiosConfig={getAxiosConfig}
        />
      )}
      {tab === 'broadcast' && (
        <BroadcastTab API_URL={API_URL} getAxiosConfig={getAxiosConfig} />
      )}
      {editing !== null && (
        <CampaignEditor
          API_URL={API_URL}
          getAxiosConfig={getAxiosConfig}
          conditions={conditions}
          campaign={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
      </div>
    </AdminPage>
  );
}

function CampaignsTab({ campaigns, loading, onEdit, onNew, onDeleted, API_URL, getAxiosConfig }) {
  const [deleting, setDeleting] = useState(null); // campaign object pending delete confirm

  if (loading) return <p>Loading…</p>;
  return (
    <>
      <Button variant="primary" onClick={onNew}>+ New campaign</Button>
      <table className="drip-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Audience</th>
            <th>Delay</th>
            <th>Skip if</th>
            <th>Active</th>
            <th>Sent / Skipped / Failed</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {campaigns.length === 0 && (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', color: '#64748b', padding: '24px' }}>
                No campaigns yet. Click "+ New campaign" to create one.
              </td>
            </tr>
          )}
          {campaigns.map((c) => (
            <tr key={c.id}>
              <td>{c.name}</td>
              <td>{audienceLabel(c.audience_role)}</td>
              <td>{formatDelay(c.delay_seconds)}</td>
              <td>{c.skip_condition_key || '—'}</td>
              <td>{c.is_active ? '✓' : '✗'}</td>
              <td>
                {c.counts?.sent ?? 0} / {c.counts?.skipped ?? 0} / {c.counts?.failed ?? 0}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <Button variant="secondary" size="sm" onClick={() => onEdit(c)}>Edit</Button>{' '}
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setDeleting(c)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {deleting && (
        <DeleteConfirmModal
          campaign={deleting}
          API_URL={API_URL}
          getAxiosConfig={getAxiosConfig}
          onCancel={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            onDeleted();
          }}
        />
      )}
    </>
  );
}

function DeleteConfirmModal({ campaign, API_URL, getAxiosConfig, onCancel, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState(null);

  const totalEnrollments =
    (campaign.counts?.pending ?? 0) +
    (campaign.counts?.sent ?? 0) +
    (campaign.counts?.skipped ?? 0) +
    (campaign.counts?.failed ?? 0);

  const handleDelete = async () => {
    setDeleting(true);
    setErr(null);
    try {
      await axios.delete(`${API_URL}/admin/drip-campaigns/${campaign.id}`, getAxiosConfig());
      onDeleted();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setDeleting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Delete campaign?</h2>
        <p>
          You're about to permanently delete <strong>{campaign.name}</strong>.
        </p>
        {totalEnrollments > 0 && (
          <p className="note">
            This will also remove <strong>{totalEnrollments} enrollment{totalEnrollments === 1 ? '' : 's'}</strong>{' '}
            (pending: {campaign.counts?.pending ?? 0}, sent: {campaign.counts?.sent ?? 0},
            skipped: {campaign.counts?.skipped ?? 0}, failed: {campaign.counts?.failed ?? 0}).
          </p>
        )}
        <p style={{ color: '#64748b', fontSize: '13px' }}>
          To keep history, use the campaign's <strong>Active</strong> toggle instead.
          Deletion cannot be undone.
        </p>
        {err && <div className="error">{err}</div>}
        <div className="actions">
          <Button variant="secondary" onClick={onCancel} disabled={deleting}>Cancel</Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : `Delete ${campaign.name}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function formatDelay(sec) {
  if (sec == null) return '—';
  if (sec % 86400 === 0) return `${sec / 86400} day(s)`;
  if (sec % 3600 === 0) return `${sec / 3600} hour(s)`;
  return `${Math.round(sec / 60)} min`;
}

function CampaignEditor({ API_URL, getAxiosConfig, conditions, campaign, onClose, onSaved }) {
  const isNew = !campaign.id;
  const [name, setName] = useState(campaign.name || '');
  const [role, setRole] = useState(campaign.audience_role || 'athlete');
  const [delayNum, setDelayNum] = useState(
    campaign.delay_seconds ? campaign.delay_seconds / 3600 : 24
  );
  const [delayUnit, setDelayUnit] = useState('Hours');
  const [body, setBody] = useState(campaign.body || '');
  const [cond, setCond] = useState(campaign.skip_condition_key || '');
  const [active, setActive] = useState(campaign.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payload = {
        name,
        audience_role: role,
        delay_seconds: Math.round(parseFloat(delayNum) * UNIT_SECONDS[delayUnit]),
        body,
        skip_condition_key: cond || null,
        is_active: active,
      };
      if (isNew) {
        await axios.post(`${API_URL}/admin/drip-campaigns`, payload, getAxiosConfig());
      } else {
        await axios.patch(
          `${API_URL}/admin/drip-campaigns/${campaign.id}`,
          payload,
          getAxiosConfig()
        );
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isNew ? 'New campaign' : `Edit: ${campaign.name}`}</h2>
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          Audience
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            {AUDIENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label>
          Delay
          <span className="delay-row">
            <input
              type="number"
              min="0"
              value={delayNum}
              onChange={(e) => setDelayNum(e.target.value)}
            />
            <select value={delayUnit} onChange={(e) => setDelayUnit(e.target.value)}>
              {Object.keys(UNIT_SECONDS).map((u) => (
                <option key={u}>{u}</option>
              ))}
            </select>
          </span>
        </label>
        <label>
          Body
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} />
        </label>
        <small>URLs like https://app.prozpects.com/profile will be tappable in the message.</small>
        <label>
          Skip if
          <select value={cond} onChange={(e) => setCond(e.target.value)}>
            <option value="">(none)</option>
            {conditions.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className="inline">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          Active
        </label>
        <p className="note">
          Body changes apply to <strong>all pending sends</strong>. Delay and audience changes
          only affect users who sign up after saving.
        </p>
        {err && <div className="error">{err}</div>}
        <div className="actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Broadcast progress polling. The fan-out runs server-side in a background
// thread, so these bounds govern only how long the PAGE watches it — giving up
// here never stops the send.
const POLL_INTERVAL_MS = 2000;
// Generous but finite. The server caps an audience at BROADCAST_MAX_RECIPIENTS
// (5000) with a 3s per-send timeout, so a pathological run could in theory
// exceed this; that is the case the "still running" notice below is for.
const POLL_DEADLINE_MS = 30 * 60 * 1000;
// A single failed poll means the network hiccuped, NOT that the broadcast died.
// Only a sustained run of failures is worth surfacing.
const POLL_MAX_CONSECUTIVE_ERRORS = 5;

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

function BroadcastTab({ API_URL, getAxiosConfig }) {
  const [role, setRole] = useState('athlete');
  const [body, setBody] = useState('');
  const [count, setCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [err, setErr] = useState(null);
  const [confirming, setConfirming] = useState(false);
  // Set when we stop watching before the broadcast reported 'done'. Not an
  // error state — the send is still going server-side.
  const [pollLapsed, setPollLapsed] = useState(false);

  // The poll loop lives in an async event handler, not an effect, so it has no
  // teardown of its own. This is how it learns the admin navigated away —
  // without it the loop keeps polling and calls setState on an unmounted tab.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    setCount(null);
    axios
      .get(`${API_URL}/admin/drip-campaigns/audience-count?role=${role}`, getAxiosConfig())
      .then((r) => setCount(r.data.count))
      .catch(() => setCount(null));
  }, [API_URL, role]);

  const requestSend = () => {
    if (count == null) return;
    setConfirming(true);
  };

  const send = async () => {
    setConfirming(false);
    setSending(true);
    setErr(null);
    setResult(null);
    setProgress(null);
    setPollLapsed(false);
    try {
      // The broadcast now runs in the background (returns immediately so it
      // can't 504 on large audiences). Poll the status endpoint for progress.
      const r = await axios.post(
        `${API_URL}/admin/drip-campaigns/broadcast`,
        { audience_role: role, body },
        getAxiosConfig()
      );
      const { broadcast_log_id, target_count, dry_run } = r.data;
      setProgress({ recipient_count: 0, failed_count: 0, target_count, status: 'running', dry_run });

      // Watch until the broadcast reports 'done' or we hit the deadline. Every
      // exit path here is a decision about the PAGE only; the send continues
      // regardless, which is why nothing below reports failure.
      const deadline = Date.now() + POLL_DEADLINE_MS;
      let consecutiveErrors = 0;
      let done = false;
      while (Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        if (!mountedRef.current) return;

        let s;
        try {
          s = await axios.get(
            `${API_URL}/admin/drip-campaigns/broadcast/${broadcast_log_id}`,
            getAxiosConfig()
          );
          consecutiveErrors = 0;
        } catch {
          // A dropped poll says nothing about the broadcast. Keep watching
          // until the failures are sustained enough to mean something.
          consecutiveErrors += 1;
          if (consecutiveErrors >= POLL_MAX_CONSECUTIVE_ERRORS) break;
          continue;
        }

        if (!mountedRef.current) return;
        setProgress({ ...s.data, dry_run });
        if (s.data.status === 'done') {
          setResult({ ...s.data, dry_run, broadcast_log_id });
          done = true;
          break;
        }
      }
      if (!done && mountedRef.current) setPollLapsed(true);
    } catch (e) {
      if (mountedRef.current) setErr(e.response?.data?.detail || e.message);
    } finally {
      if (mountedRef.current) setSending(false);
    }
  };

  return (
    <div className="broadcast">
      <label>
        Audience
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          {AUDIENCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>
      {count !== null && (
        <p>
          Will send to <strong>{count}</strong> {audienceLabel(role)} recipient{count === 1 ? '' : 's'}.
        </p>
      )}
      <label>
        Body
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} />
      </label>
      <Button
        variant="primary"
        onClick={requestSend}
        disabled={sending || !body || !count}
      >
        {sending ? 'Sending…' : 'Send now'}
      </Button>
      {err && <div className="error">{err}</div>}
      {progress && !result && (
        <div className="info">
          {progress.dry_run ? "DRY RUN — " : ""}Sending… {progress.recipient_count}
          {progress.target_count ? ` / ${progress.target_count}` : ""}
          {progress.failed_count > 0 && ` (failed: ${progress.failed_count})`}
          {/* Suppressed once polling lapses — the notice below says it better,
              and two "still running" lines read as a contradiction. */}
          {!pollLapsed && (
            <div style={{ fontSize: "12px", marginTop: "4px", color: "#64748b" }}>
              Running in the background — safe to leave this page; it won't stop.
            </div>
          )}
        </div>
      )}
      {/* Not an error: we stopped watching, the server did not stop sending. */}
      {pollLapsed && !result && (
        <div className="info">
          Stopped tracking progress — the broadcast is still running on the server.
          <div style={{ fontSize: "12px", marginTop: "4px", color: "#64748b" }}>
            Reload this page to pick up the final count.
          </div>
        </div>
      )}
      {result && (
        <div className={result.dry_run ? "info" : (result.recipient_count === 0 && result.failed_count > 0 ? "error" : "success")}>
          {result.dry_run ? "DRY RUN — " : ""}
          {result.dry_run ? "Would have sent to " : "Sent to "}
          {result.recipient_count} user{result.recipient_count === 1 ? "" : "s"}.
          {result.failed_count > 0 && ` (failed: ${result.failed_count})`}
          {result.last_error && (
            <div style={{ fontSize: "12px", marginTop: "4px" }}>
              First error: {result.last_error}
            </div>
          )}
          {result.dry_run && (
            <div style={{fontSize: "12px", marginTop: "4px"}}>
              Audit log row {result.broadcast_log_id} was written. No actual sends fired.
            </div>
          )}
        </div>
      )}
      {confirming && (
        <BroadcastConfirmModal
          count={count}
          body={body}
          onConfirm={send}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

function BroadcastConfirmModal({ count, body, onConfirm, onCancel }) {
  const preview = body.length > 200 ? `${body.slice(0, 200)}…` : body;
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Confirm broadcast</h2>
        <p>
          This will send an in-app message to <strong>{count}</strong> user
          {count === 1 ? '' : 's'}. This can't be undone.
        </p>
        <label>
          Message preview
          <div className="broadcast-preview">{preview}</div>
        </label>
        <div className="actions">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={onConfirm}>
            Send to {count} user{count === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </div>
  );
}
